import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Recipe } from "@/models/cookbook";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AppMenu } from "@/components/navigation/AppMenu";
import { LanguageSelector } from "@/components/decision/LanguageSelector";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Search, Clock, ChefHat, Utensils } from "lucide-react";

const Cookbook = () => {
    const [recipes, setRecipes] = useState<Recipe[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");

    useEffect(() => {
        const fetchRecipes = async () => {
            try {
                const { data, error } = await supabase
                    .from("recipes")
                    .select("*")
                    .order("created_at", { ascending: false });

                if (error) {
                    throw error;
                }

                if (data) {
                    setRecipes(data);
                }
            } catch (error) {
                console.error("Error fetching recipes:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchRecipes();
    }, []);

    const filteredRecipes = recipes.filter(recipe =>
        recipe.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        recipe.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        recipe.category?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="min-h-screen bg-background text-foreground p-4 md:p-8">
            <div className="absolute top-4 right-4 grid grid-cols-2 gap-2 z-10">
                <LanguageSelector />
                <AppMenu />
            </div>

            <div className="max-w-4xl mx-auto mt-16">
                <h1 className="text-4xl font-bold mb-8 text-center">Cookbook</h1>

                <div className="relative mb-8 max-w-md mx-auto">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                    <Input
                        type="text"
                        placeholder="Search recipes..."
                        className="pl-10"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </div>

                {loading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {[1, 2, 3, 4].map((i) => (
                            <Card key={i} className="overflow-hidden">
                                <Skeleton className="h-48 w-full" />
                                <CardHeader>
                                    <Skeleton className="h-6 w-3/4" />
                                    <Skeleton className="h-4 w-1/2" />
                                </CardHeader>
                            </Card>
                        ))}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {filteredRecipes.map((recipe) => (
                            <Link to={`/cookbook/${recipe.id}`} key={recipe.id}>
                                <Card className="overflow-hidden hover:shadow-lg transition-shadow duration-300 h-full cursor-pointer flex flex-col">
                                    {recipe.image_url && (
                                        <div className="h-48 overflow-hidden">
                                            <img
                                                src={recipe.image_url}
                                                alt={recipe.title}
                                                className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                                            />
                                        </div>
                                    )}
                                    <CardHeader>
                                        <div className="flex justify-between items-start">
                                            <CardTitle className="line-clamp-1">{recipe.title}</CardTitle>
                                            {recipe.category && (
                                                <span className="text-xs font-medium px-2 py-1 bg-secondary rounded-full whitespace-nowrap ml-2">
                                                    {recipe.category}
                                                </span>
                                            )}
                                        </div>
                                        <CardDescription className="line-clamp-2">{recipe.description}</CardDescription>
                                    </CardHeader>
                                    <CardContent className="mt-auto">
                                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                                            {recipe.cooking_time && (
                                                <div className="flex items-center gap-1">
                                                    <Clock className="h-4 w-4" />
                                                    <span>{recipe.cooking_time} min</span>
                                                </div>
                                            )}
                                            {recipe.difficulty && (
                                                <div className="flex items-center gap-1">
                                                    <ChefHat className="h-4 w-4" />
                                                    <span>{recipe.difficulty}</span>
                                                </div>
                                            )}
                                        </div>
                                    </CardContent>
                                </Card>
                            </Link>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Cookbook;
